import { computed, type Signal, signal, inject, SecurityContext, type WritableSignal } from "@angular/core";
import { DomSanitizer, type SafeHtml } from "@angular/platform-browser";
import { WkTranslateService, UtilsService } from "@shared/utils";
import type {
    WkDataTableColumnDefinition,
    WkDataTableColumnValue,
    WkDataTableCellValue,
    WkDataTableStateColumn,
} from "@components/data-table/data-table.types";
import { WkDataTableSortDirectionEnum } from "@components/data-table/data-table.types";
import { WkDataTableColumn } from "./data-table-column";
import { WkDataTableSortService } from "@app/shared/components/data-table/item";
import { WkDataTableSearchService } from "@app/shared/components/data-table/searchbar";

export abstract class WkDataTableSimpleColumn<
    TItem,
    TValue extends WkDataTableCellValue,
    TSortValue extends WkDataTableCellValue = TValue,
    TColumnDefinition extends WkDataTableColumnDefinition = WkDataTableColumnDefinition,
> extends WkDataTableColumn<TColumnDefinition> {
    protected readonly sanitizer = inject(DomSanitizer);
    private readonly dataTableSearchService = inject(WkDataTableSearchService);
    private readonly dataTableSortService = inject(WkDataTableSortService);
    protected readonly wkTranslate = inject(WkTranslateService);

    protected active?: Signal<boolean>;
    protected abstract value(): WkDataTableColumnValue<TItem, TValue> | null;
    protected sortValue?: () => WkDataTableColumnValue<TItem, TSortValue> | null;
    protected abstract getDefinitionInfo(): TColumnDefinition;
    protected title?: Signal<string>;

    protected override createDefinition(): TColumnDefinition {
        const item = this.getDefinitionInfo();
        return {
            resource: undefined,
            export: true,
            width: 170,
            locked: false,
            reorderable: true,
            sortable: true,
            ...item,
        } as TColumnDefinition;
    }

    private readonly _position = lazySignal(() => this.definition.position ?? 0);
    private readonly _visible = lazySignal(() => this.definition.visible ?? true);
    private readonly _sorted = lazySignal(() => this.definition.sorted ?? false);
    private readonly _sortDirection = lazySignal(() => this.definition.sortDirection ?? WkDataTableSortDirectionEnum.Asc);
    private _sortOrder = signal<number>(0);

    public readonly isActive = computed(() => this.active?.() ?? true);
    public readonly isVisible = computed(() => this._visible()());
    public readonly position = computed(() => this._position()());
    public readonly isSorted = computed(() => this._sorted()());
    public readonly sortDirection = computed(() => this._sortDirection()());
    public readonly sortOrder = this._sortOrder.asReadonly();

    public setState(state: Partial<WkDataTableStateColumn>) {
        if ("position" in state && state.position != null) this._position().set(state.position);
        if ("visible" in state && state.visible) this._visible().set(state.visible);
        if ("sorted" in state && state.sorted) this._sorted().set(state.sorted);
        if ("sortDirection" in state && state.sortDirection) this._sortDirection().set(state.sortDirection);
        if ("sortOrder" in state && state.sortOrder != null) this._sortOrder.set(state.sortOrder);
    }

    public state(): WkDataTableStateColumn {
        return {
            position: this.position(),
            visible: this.isVisible(),
            sorted: this.isSorted(),
            sortDirection: this.sortDirection(),
            sortOrder: this.sortOrder(),
        };
    }

    public clearState() {
        this._position().set(this.definition.position ?? 0);
        this._visible().set(this.definition.visible ?? true);
        this._sorted().set(this.definition.sorted ?? false);
        this._sortDirection().set(this.definition.sortDirection ?? WkDataTableSortDirectionEnum.Asc);
        this._sortOrder.set(0);
    }

    public getStringValue(): WkDataTableColumnValue<TItem, string> {
        const getValue = this.value();
        return (item: TItem): string => {
            const value = getValue?.(item);
            if (!value) return "";
            if (this.isSafeValue(value)) return (this.sanitizer.sanitize(SecurityContext.HTML, value) ?? "").replace(/<[^>]*>/g, "");
            return String(value ?? "");
        };
    }

    public isSafeValue(value: unknown): value is SafeHtml {
        return typeof value === "object" && value !== null;
    }

    public getValue(): WkDataTableColumnValue<TItem, SafeHtml | string> {
        const getValue = this.value();
        const search = this.dataTableSearchService.searchValue();
        return (item: TItem): SafeHtml | string => {
            const value = getValue?.(item);
            if (!value) return "";
            const strValue = String(value);
            if (!this.definition.searchable || !search) {
                if (!UtilsService.containsHtml(strValue)) return strValue;
                return this.sanitizer.bypassSecurityTrustHtml(strValue);
            }
            const formattedValue = this.highlight(strValue, search);
            return this.sanitizer.bypassSecurityTrustHtml(formattedValue);
        };
    }

    public getInputValue(): WkDataTableColumnValue<TItem, TValue> | null {
        const getValue = this.value();
        return (item: TItem): TValue => (getValue?.(item) ?? null) as TValue;
    }

    public getSortValue(): WkDataTableColumnValue<TItem, TSortValue> | null {
        const getValue = this.sortValue?.() ?? this.value();
        return (item: TItem): TSortValue => (getValue?.(item) ?? null) as TSortValue;
    }

    private escapeHtml(text: string): string {
        return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    }

    private highlight(text: string, search: string): string {
        if (!search) return this.escapeHtml(text);
        const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(${escaped})`, "gi");
        // With a capturing group, split places the matches at odd indices.
        const parts = text.split(regex);

        return parts
            .map((part, index) => {
                const safePart = this.escapeHtml(part);
                const value = index % 2 === 1 ? `<span class="highlight">${safePart}</span>` : safePart;
                console.log("[WK] Highlighting value:", value, "safePart:", safePart, "index:", index);
                return value;
            })
            .join("");
    }

    public onColumnHeaderClicked(event: MouseEvent | KeyboardEvent): void {
        if (!this.definition.sortable) return;

        const sorting = this.dataTableSortService.sorting();
        const columns = this.dataTableSortService.columns();

        const useMulti = sorting?.multiple && !!(event && "shiftKey" in event && (event as MouseEvent | KeyboardEvent).shiftKey);

        const sorted = this.isSorted();
        const sortDirection = this.sortDirection();
        if (sorted) {
            this._sortDirection().set(
                sortDirection === WkDataTableSortDirectionEnum.Asc ? WkDataTableSortDirectionEnum.Desc : WkDataTableSortDirectionEnum.Asc,
            );
        } else {
            this._sorted().set(true);
            this._sortOrder.set(!useMulti ? 0 : columns.length);
        }
    }

    public getTitle(): SafeHtml | string {
        const title = this.title?.() ?? this.wkTranslate.translate(this.definition.title ?? "");
        if (!title) return "";
        if (!UtilsService.containsHtml(title)) return title;
        return this.sanitizer.bypassSecurityTrustHtml(String(title ?? ""));
    }

    public fullFieldName = computed(() => {
        const fieldName = this.definition.fieldname;
        const dynamicFieldName = this.definition.dynamicFieldName;
        return dynamicFieldName ? `${fieldName}_${dynamicFieldName}` : fieldName;
    });
}

function lazySignal<T>(initialValue: () => T): () => WritableSignal<T> {
    let instance: WritableSignal<T> | undefined;

    return () => (instance ??= signal(initialValue()));
}
